import {
	CollapseIcon,
	ExpandIcon,
	StyledTreeItem,
} from './LayersTreeView.styles';
import { ConfigLayerInternal, LayerToggle, LayerType } from '@Map/types';
import React, {
	ChangeEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import {
	Visibility,
	convertVisibilityToHiddenLayers,
	getChangedVisibilityPaths,
	getDefaultVisibility,
	updateVisibility,
} from '../utils';

import LayerItem from '../LayerItem';
import { MapHiddenLayers } from '@Map/layers/LayerManager';
import { TreeView } from '@mui/x-tree-view';
import { useGlobalization } from '@Translations/useGlobalization';

const getDisplayType = (
	type: LayerType | undefined,
	iconImage: string | undefined,
) => {
	if (type === 'line' || type === 'polygon') {
		return type;
	}

	if (iconImage) {
		return 'image';
	}

	if (type) {
		return 'circle';
	}

	return 'none';
};

/** Resolves a layer node by id so EditorProvider receives config without relying on getSelected. */
function findLayerByIdInTree(
	layers: ConfigLayerInternal[],
	id: string,
): ConfigLayerInternal | undefined {
	for (const layer of layers) {
		if (layer.id === id) {
			return layer;
		}
		const children = layer.layers || layer.virtualLayers;
		if (children?.length) {
			const found = findLayerByIdInTree(children, id);
			if (found) {
				return found;
			}
		}
	}
	return undefined;
}

export interface LayersProps {
	config: ConfigLayerInternal[];
	getVisibility: (idPath: string) => boolean;
	onToggleVisibility: (idPath: string) => (state: boolean) => void;
	tabIndex?: number;
	canToggle?: boolean;
}

const NestedTreeItems = ({
	config,
	getVisibility,
	onToggleVisibility,
	tabIndex,
	canToggle = true,
}: LayersProps): JSX.Element => {
	const { t } = useGlobalization();
	return (
		<>
			{config.map(
				(
					{
						errored,
						layerName,
						layers,
						virtualLayers,
						color,
						outlineColor,
						fillOpacity,
						pattern,
						lineDashArray,
						type,
						iconImage,
						count,
						idPath,
						id,
					},
					i,
				) => {
					const label = (
						<LayerItem
							key={`layerItem-${i}`}
							onToggleVisibility={onToggleVisibility(idPath)}
							layerId={id}
							config={config[i]}
							tabIndex={tabIndex}
							layerOn={getVisibility(idPath)}
							color={color}
							outlineColor={outlineColor}
							fillOpacity={fillOpacity}
							pattern={pattern}
							lineDashArray={lineDashArray}
							displayType={getDisplayType(type, iconImage)}
							iconImage={iconImage}
							count={count}
							canToggle={canToggle}
							errored={errored}>
							{`${t(layerName)}`}
						</LayerItem>
					);

					const layersToDisplay = layers || virtualLayers;
					if (layersToDisplay?.length) {
						return (
							<StyledTreeItem
								key={id}
								nodeId={id}
								label={label}
								data-cy={`tree-item--${id}`}
								data-nested="true">
								<NestedTreeItems
									config={layersToDisplay}
									getVisibility={getVisibility}
									onToggleVisibility={onToggleVisibility}
									tabIndex={tabIndex}
									canToggle={!virtualLayers?.length}
								/>
							</StyledTreeItem>
						);
					}

					return (
						<StyledTreeItem
							key={id}
							nodeId={id}
							label={label}
							data-cy={`tree-item--${id}`}
						/>
					);
				},
			)}
		</>
	);
};

interface LayersTreeViewProps {
	config: LayersProps['config'];
	hiddenLayers?: MapHiddenLayers;
	onLayerToggle?: (args: LayerToggle) => void;
	tabIndex: number;
	onSelect?: (layerId: string, config?: ConfigLayerInternal) => void;
	selectedId?: string;
}

const LayersTreeView = ({
	config,
	hiddenLayers,
	onLayerToggle,
	tabIndex,
	onSelect,
	selectedId,
}: LayersTreeViewProps): JSX.Element => {
	const [layerConfig, setLayerConfig] = useState(config);
	const [visibility, setVisibility] = useState<Visibility>(
		config ? getDefaultVisibility(config, hiddenLayers) : {},
	);

	// Tracks the hiddenLayers array we most recently emitted via onLayerToggle.
	// When the parent round-trips that same array back as the `hiddenLayers`
	// prop, the re-seed effect below skips the reseed so local toggle state
	// is not clobbered by `config.visible` defaults baked into the theme.
	const lastEmittedHiddenLayersRef = useRef<MapHiddenLayers | undefined>(
		undefined,
	);
	// Tracks the previous `config` reference so the effect can distinguish
	// "config actually changed" (theme switch, new layers) from "only hiddenLayers
	// changed via our own round-trip".
	const previousConfigRef = useRef<typeof config | undefined>(undefined);

	const getVisibility = useCallback(
		(idPath: string) => visibility[idPath],
		[visibility],
	);

	const onToggleVisbility: LayersProps['onToggleVisibility'] = useCallback(
		idPath => state => {
			const updatedVisibility = updateVisibility(
				idPath,
				visibility,
				state,
			);
			setVisibility(updatedVisibility);
			const updatedHiddenLayers =
				convertVisibilityToHiddenLayers(updatedVisibility);
			lastEmittedHiddenLayersRef.current = updatedHiddenLayers;
			onLayerToggle?.({
				layerPath: idPath,
				state,
				hiddenLayers: updatedHiddenLayers,
				// Preserve full idPath keys to avoid collisions between similarly
				// named descendants in different groups (e.g. ".../pipe").
				layerVisibility: { ...updatedVisibility },
				changedLayerPaths: getChangedVisibilityPaths(
					visibility,
					updatedVisibility,
				),
			});
		},
		[visibility, onLayerToggle],
	);

	const onNodeSelect = (_: ChangeEvent<unknown>, layerId: string) => {
		const resolved = findLayerByIdInTree(layerConfig, layerId);
		onSelect?.(layerId, resolved);
	};

	useEffect(() => {
		const configChanged = config !== previousConfigRef.current;
		previousConfigRef.current = config;
		setLayerConfig(config);

		// Skip the re-seed when only `hiddenLayers` changed AND it matches the
		// array we just emitted. Without this guard every toggle round-trip
		// (panel -> parent -> back to panel) overwrites local visibility with
		// `config.visible` defaults, reverting the user's click for any layer
		// that has `visible: false` baked into the theme/config.
		if (
			!configChanged &&
			hiddenLayers !== undefined &&
			hiddenLayers === lastEmittedHiddenLayersRef.current
		) {
			return;
		}

		const defaultVisibility = getDefaultVisibility(config, hiddenLayers);
		setVisibility(defaultVisibility);
	}, [config, hiddenLayers]);

	return (
		<TreeView
			defaultCollapseIcon={<CollapseIcon />}
			defaultExpandIcon={<ExpandIcon />}
			onNodeSelect={onNodeSelect}
			selected={selectedId}>
			<NestedTreeItems
				config={layerConfig}
				getVisibility={getVisibility}
				onToggleVisibility={onToggleVisbility}
				tabIndex={tabIndex}
			/>
		</TreeView>
	);
};

export default React.memo(LayersTreeView);
